import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  DotGroup,
  Slide,
  Slider
} from 'pure-react-carousel'
import { ButtonLink } from '@dtx-company/inter-app/src/utils/links'
import { ClientOnly } from '../../utils/client-only'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image/index'
import { ImageCarouselProps } from './types'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import React, { useId } from 'react'
import styled from 'styled-components'

export const ImageCarouselWrapper = styled(Box)`
  display: block;
  .carousel__inner-slide {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .carousel__dot-group {
    height: 5px;
    display: flex;
    justify-content: center;
    flex-direction: row;
  }

  .carousel__dot {
    color: black;
    padding: 4px;
    margin: 5px;
    border-radius: 5px;
  }

  .carousel__next-button,
  .carousel__back-button {
    background: transparent;
    border: 0;
    padding: 0;
  }
`

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  autoplay = false,
  scrollable = false,
  mobileHeight = 275,
  slideHeight = 1, // to determine the individual slide aspect ratio
  slideWidth = 1,
  nVisibleSlides = 1,
  displayDots = false,
  displayArrows = false,
  lazyLoadImages,
  ...rest
}): JSX.Element => {
  const id = useId()
  return (
    <ClientOnly>
      <ImageCarouselWrapper {...rest}>
        <CarouselProvider
          totalSlides={images.length}
          visibleSlides={nVisibleSlides}
          infinite={true}
          interval={3000}
          isPlaying={autoplay}
          touchEnabled={scrollable}
          dragEnabled={scrollable}
          naturalSlideHeight={slideHeight}
          naturalSlideWidth={slideWidth}
        >
          <Box justifyContent="space-between">
            {displayArrows && (
              <ButtonBack aria-controls={id}>
                <Icon src="/static/images/plans/plans-arrow-left.svg" width="20px" alt="previous" />
              </ButtonBack>
            )}
            <Box display="block" width="100%">
              <Slider
                aria-label="Carousel"
                id={id}
                role={undefined}
                trayProps={{
                  role: 'region',
                  'aria-roledescription': 'carousel',
                  'aria-label': 'carousel'
                }}
              >
                {images.map((image, index) => (
                  <Slide
                    index={index}
                    key={index}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} of ${images.length}`}
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      margin="auto"
                      flexDirection="column"
                      {...image.slideWrapperProps}
                    >
                      <Image src={image.src} alt={image.alt} lazyLoad={lazyLoadImages} />
                      {image.link && image.caption && (
                        <ButtonLink
                          href={image.link}
                          sizeVariant="invisible"
                          colorVariant="invisible"
                          wrapperProps={{ margin: 'auto' }}
                        >
                          <Text
                            padding="15px 0"
                            variant={'body/small'}
                            textAlign="center"
                            fontWeight="bold"
                          >
                            {image.caption}
                          </Text>
                        </ButtonLink>
                      )}
                    </Box>
                  </Slide>
                ))}
              </Slider>
              {displayDots && <DotGroup showAsSelectedForCurrentSlideOnly={true}></DotGroup>}
            </Box>
            {displayArrows && (
              <ButtonNext aria-controls={id}>
                <Icon src="/static/images/plans/plans-arrow-right.svg" width="20px" alt="next" />
              </ButtonNext>
            )}
          </Box>
        </CarouselProvider>
      </ImageCarouselWrapper>
    </ClientOnly>
  )
}
